import { isEmpty } from 'lodash';
import { getDateInRequestFormat } from 'utils/dateUtil';
import {
  DirectSalesProductRules,
  DirectSalesProductTemplateBehaviour,
  DirectSalesProductRuleAllowedSet,
  DirectSalesProductRuleFixedSet,
  DirectSalesAllowedSet,
  DirectSalesFixedSet,
  productRuleMap,
  DirectSalesProductTemplateChannelFilter,
  DirectSalesProductTemplateRuleType,
  DirectSalesProductTemplateMediaFilter,
  DirectSalesProductTemplateTargetFilter,
  DirectSalesProductFilter,
  DirectSalesProductTemplateNetworkFilter,
  DirectSalesBehaviourValueKey,
  DirectSalesProductRuleDefaultSet,
  DirectSalesProductPoiFilter,
  DirectSalesProductTemplateCustomPoiFilterItem,
  DirectSalesProductTemplateFrameListFilter,
  DirectSalesRangeSet,
  DirectSalesProductRuleRangeSet,
  DirectSalesProductSweepFilter,
  DirectSalesProductSotFixedFilter,
} from 'components/common/types/DirectSalesProductTemplate.types';
import { ProductManagementFields, ProductTarget, SelectionPermission } from 'store/productManagement/reducer.types';
import { transformObjectivesRequest } from 'components/pages/Planner/hooks/utils/transformDirectSalesCampaignRequests';
import { mapLocationFilter, mappedFormatFilterName } from 'components/pages/Planner/hooks/utils/transformFilters';
import {
  DirectSalesCodeNameModel,
  DirectSalesFilterScope,
  DirectSalesTargetCriteria,
} from 'components/common/types/DirectSalesCampaign.types';
import { FormatLabel } from 'store/userOptions/reducer.types';
import { NetworkCriteriaType } from 'components/common/types/DirectSalesNetworks.types';
import { buildCustomPoiFilterValue, transformPoiToFilter } from 'utils/transformPoi';
import { CUSTOM_POI_DATA_PROVIDER } from 'components/pages/Planner/PlannerSections/DataTargetingContent/PointsOfInterest/consts';

export const filterBySelectionPermission = (
  productTemplate: Partial<ProductManagementFields>,
  permission: SelectionPermission,
): Partial<ProductManagementFields> => {
  return Object.fromEntries(
    Object.entries(productTemplate).filter(([, value]) => value?.selectionPermission === permission),
  );
};

export const filterAnyDisabledValues = (
  productTemplate: Partial<ProductManagementFields>,
): Partial<ProductManagementFields> => {
  return Object.fromEntries(
    Object.entries(productTemplate).filter(
      ([, value]) => value !== undefined && value.selectionPermission !== SelectionPermission.DISABLED,
    ),
  );
};

export const transformNoRestrictionOptions = (fields: Partial<ProductManagementFields>): DirectSalesProductRules[] => {
  return Object.keys(fields).map((fieldName) => {
    return {
      ruleType: productRuleMap[fieldName] as DirectSalesProductTemplateRuleType,
      behaviour: DirectSalesProductTemplateBehaviour.ANY,
    };
  });
};

const transformChannel = (fields: Partial<ProductManagementFields>): DirectSalesProductTemplateChannelFilter[] => {
  const fieldValue = fields.channels?.value;

  if (!fieldValue || !fieldValue?.length) {
    return [];
  }

  return fieldValue.map(({ code, name }) => ({
    type: productRuleMap.channels as DirectSalesProductTemplateRuleType,
    name,
    id: code,
  }));
};

const transformPricingMode = (fields: Partial<ProductManagementFields>): string[] => {
  const fieldValue = fields.pricingMode?.value;

  if (!fieldValue) {
    return [];
  }

  return [fieldValue] as string[];
};

const transformMediaType = (fields: Partial<ProductManagementFields>): DirectSalesProductTemplateMediaFilter[] => {
  const fieldValue = fields.mediaType?.value;

  if (!fieldValue) {
    return [];
  }

  return [
    {
      type: productRuleMap.mediaType,
      mediaType: fieldValue,
    },
  ];
};

const transformTarget = (target: ProductTarget): DirectSalesProductTemplateTargetFilter[] => {
  if (
    !target.value ||
    target.greenTolerance === undefined ||
    target.orangeTolerance === undefined ||
    !target.priority
  ) {
    return [];
  }

  return [
    {
      target: target.value,
      defaultGreenTolerance: target.greenTolerance / 100,
      defaultOrangeTolerance: target.orangeTolerance / 100,
      defaultPriority: Number(target.priority.code),
    },
  ];
};

const transformBudget = (fields: Partial<ProductManagementFields>): DirectSalesProductTemplateTargetFilter[] => {
  const fieldValue = fields?.budget;

  if (!fieldValue) {
    return [];
  }

  return transformTarget(fieldValue);
};

const transformFrames = (fields: Partial<ProductManagementFields>): DirectSalesProductTemplateTargetFilter[] => {
  const fieldValue = fields?.frames;

  if (!fieldValue) {
    return [];
  }

  return transformTarget(fieldValue);
};

const transformImpressions = (fields: Partial<ProductManagementFields>): DirectSalesProductTemplateTargetFilter[] => {
  const fieldValue = fields?.impressions;

  if (!fieldValue) {
    return [];
  }

  return transformTarget(fieldValue);
};

const transformNetworks = (fields: Partial<ProductManagementFields>): DirectSalesProductTemplateNetworkFilter[] => {
  const fieldValue = fields?.networks?.value;

  if (!fieldValue) {
    return [];
  }

  return fieldValue.map(({ target, priority, code, name, greenTolerance, orangeTolerance }) => ({
    networkName: name,
    target: Number(target),
    defaultPriority: Number(priority.code),
    networkId: code,
    defaultGreenTolerance: greenTolerance / 100,
    defaultOrangeTolerance: orangeTolerance / 100,
  }));
};

const transformFormats = (uniqueCategoryValues: DirectSalesCodeNameModel[]): DirectSalesProductFilter[] => {
  return uniqueCategoryValues.map(({ category, include, name, code }) => ({
    type: category ? mappedFormatFilterName[category] : '',
    selectionType: include ? DirectSalesFilterScope.INCLUDE : DirectSalesFilterScope.EXCLUDE,
    name,
    id: code,
  }));
};

const transformLocations = (uniqueCategoryValues: DirectSalesCodeNameModel[]): DirectSalesProductFilter[] => {
  return uniqueCategoryValues.map(({ category, include, name, code }) => ({
    type: category ? mapLocationFilter[category] : '',
    selectionType: include ? DirectSalesFilterScope.INCLUDE : DirectSalesFilterScope.EXCLUDE,
    name,
    id: code,
  }));
};

const transformPoi = (fields: Partial<ProductManagementFields>): DirectSalesProductPoiFilter[] => {
  const fieldValue = fields?.poi?.value;

  if (!fieldValue) {
    return [];
  }

  return fieldValue.filter((poi) => poi.dataProvider !== CUSTOM_POI_DATA_PROVIDER).map(transformPoiToFilter);
};

export const transformCustomPoiValues = (
  fields: Partial<ProductManagementFields>,
): DirectSalesProductRuleFixedSet[] => {
  const customPois = fields?.poi?.value?.filter((poi) => poi.dataProvider === CUSTOM_POI_DATA_PROVIDER);

  if (!customPois?.length) return [];

  const fixedSet = customPois.reduce<DirectSalesProductTemplateCustomPoiFilterItem[]>((acc, poi) => {
    const selectionType = poi.include ? DirectSalesFilterScope.INCLUDE : DirectSalesFilterScope.EXCLUDE;
    const existing = acc.find((item) => item.id === poi.code && item.selectionType === selectionType);

    const value = buildCustomPoiFilterValue(poi);

    if (existing) {
      existing.values.push(value);
    } else {
      acc.push({ type: 'CustomPoiFilter', id: poi.code, selectionType, values: [value] });
    }

    return acc;
  }, []);

  return [
    {
      ruleType: 'CustomPoiFilter' as DirectSalesProductTemplateRuleType,
      behaviour: DirectSalesProductTemplateBehaviour.FIXED,
      fixedSet,
    },
  ];
};

const transformTags = (fields: Partial<ProductManagementFields>): DirectSalesProductFilter[] => {
  const fieldValue = fields?.tags?.value;

  if (!fieldValue) {
    return [];
  }

  return fieldValue.map(({ include, code, name }) => ({
    type: 'TagFilter',
    selectionType: include ? DirectSalesFilterScope.INCLUDE : DirectSalesFilterScope.EXCLUDE,
    id: code,
    name,
  }));
};

const transformSweep = (fields: Partial<ProductManagementFields>): DirectSalesProductSweepFilter[] => {
  const fieldValue = fields?.sweep?.value;

  if (!fieldValue) {
    return [];
  }

  return [fieldValue];
};

const transformSot = (fields: Partial<ProductManagementFields>): DirectSalesProductSotFixedFilter[] => {
  const fieldValue = fields?.sot?.value;

  if (!fieldValue || !fieldValue.defaultValue) {
    return [];
  }

  return [{ type: 'SotTarget', target: fieldValue.defaultValue / 100 }];
};

export const transformFixedSetRuleBasedOnField = (
  fields: Partial<ProductManagementFields>,
  fieldName: string,
): DirectSalesFixedSet => {
  if (isEmpty(fields)) return [];

  if (fieldName === 'channels') {
    return transformChannel(fields);
  }

  if (fieldName === 'pricingMode') {
    return transformPricingMode(fields);
  }

  if (fieldName === 'mediaType' && fields[fieldName]?.value) {
    return transformMediaType(fields);
  }

  if (fieldName === 'budget') {
    return transformBudget(fields);
  }

  if (fieldName === 'frames') {
    return transformFrames(fields);
  }

  if (fieldName === 'impressions') {
    return transformImpressions(fields);
  }

  if (fieldName === 'networks') {
    return transformNetworks(fields);
  }

  if (fieldName === 'poi') {
    return transformPoi(fields);
  }

  if (fieldName === 'tags') {
    return transformTags(fields);
  }

  if (fieldName === 'sweep') {
    return transformSweep(fields);
  }

  if (fieldName === 'sot') {
    return transformSot(fields);
  }

  return [];
};

export const transformFixedValues = (fields: Partial<ProductManagementFields>): DirectSalesProductRuleFixedSet[] => {
  return Object.keys(fields)
    .map((fieldName) => ({
      ruleType: productRuleMap[fieldName] as DirectSalesProductTemplateRuleType,
      behaviour: DirectSalesProductTemplateBehaviour.FIXED,
      fixedSet: transformFixedSetRuleBasedOnField(fields, fieldName),
    }))
    .filter((rule) => rule.fixedSet.length > 0);
};

export const transformFixedFormatValues = (
  fields: Partial<ProductManagementFields>,
): DirectSalesProductRuleFixedSet[] => {
  if (fields.formats && fields.formats.selectionPermission === SelectionPermission.FIXED_VALUE) {
    const fieldValue = fields?.formats?.value;

    if (!fieldValue) {
      return [];
    }

    const uniqueCategories = [...new Set(fieldValue.map(({ category }) => category))];

    if (!uniqueCategories) return [];

    const uniqueCategoryValues = uniqueCategories.map((category) =>
      fieldValue.map((item) => (item.category === category ? item : null)).filter(Boolean),
    );

    return uniqueCategories.flatMap((uniqueCategory) => {
      const currentUniqueCategoryValues = uniqueCategoryValues.filter((formatCategory: DirectSalesCodeNameModel[]) => {
        if (!formatCategory?.length) return [];
        return formatCategory.find(({ category }) => category === uniqueCategory);
      })[0];

      return {
        ruleType: mappedFormatFilterName[uniqueCategory as string] as DirectSalesProductTemplateRuleType,
        behaviour: DirectSalesProductTemplateBehaviour.FIXED,
        fixedSet: transformFormats(currentUniqueCategoryValues as DirectSalesCodeNameModel[]),
      };
    });
  }

  return [];
};

export const transformFixedLocationValues = (
  fields: Partial<ProductManagementFields>,
): DirectSalesProductRuleFixedSet[] => {
  if (fields.locations && fields.locations.selectionPermission === SelectionPermission.FIXED_VALUE) {
    const fieldValue = fields?.locations?.value;

    if (!fieldValue) {
      return [];
    }

    const uniqueCategories = [...new Set(fieldValue.map(({ category }) => category))];

    if (!uniqueCategories) return [];

    const uniqueCategoryValues = uniqueCategories.map((category) =>
      fieldValue.map((item) => (item.category === category ? item : null)).filter(Boolean),
    );

    return uniqueCategories.flatMap((uniqueCategory) => {
      const currentUniqueCategoryValues = uniqueCategoryValues.filter(
        (locationCategory: DirectSalesCodeNameModel[]) => {
          if (!locationCategory?.length) return [];
          return locationCategory.find(({ category }) => category === uniqueCategory);
        },
      )[0];

      return {
        ruleType: mapLocationFilter[uniqueCategory as string] as DirectSalesProductTemplateRuleType,
        behaviour: DirectSalesProductTemplateBehaviour.FIXED,
        fixedSet: transformLocations(currentUniqueCategoryValues as DirectSalesCodeNameModel[]),
      };
    });
  }

  return [];
};

export const transformAllowedSetRuleBasedOnField = (
  fields: Partial<ProductManagementFields>,
  fieldName: string,
): DirectSalesAllowedSet => {
  if (fieldName === 'range' && fields[fieldName]?.value) {
    return [
      {
        startDate: getDateInRequestFormat(fields[fieldName]!.value?.startDate as Date, [0, 0, 0, 0]),
        endDate: getDateInRequestFormat(fields[fieldName]!.value?.endDate as Date, [23, 59, 59, 999]),
        floatingDateRange: null,
      },
    ];
  }

  return [];
};

const transformTargetRange = (
  fields: Partial<ProductManagementFields>,
  fieldName: keyof ProductManagementFields,
): DirectSalesRangeSet => {
  const { greenTolerance, orangeTolerance, priority, minValue, maxValue, defaultValue } = fields[
    fieldName
  ] as ProductTarget;
  const range: Record<string, number | undefined> = {
    ...(minValue !== undefined && { min: minValue }),
    ...(maxValue !== undefined && { max: maxValue }),
    ...(defaultValue !== undefined && { default: defaultValue }),
    ...(greenTolerance !== undefined && { defaultGreenTolerance: greenTolerance / 100 }),
    ...(orangeTolerance !== undefined && { defaultOrangeTolerance: orangeTolerance / 100 }),
    ...(priority && { defaultPriority: Number(priority.code) }),
  };

  return [range];
};

export const transformNetworksRange = (networks: NetworkCriteriaType[]): DirectSalesRangeSet => {
  return networks.map(
    ({ name, code, minValue, maxValue, defaultValue, greenTolerance, orangeTolerance, priority }) => ({
      networkName: name,
      networkId: code,
      defaultGreenTolerance: greenTolerance / 100,
      defaultOrangeTolerance: orangeTolerance / 100,
      defaultPriority: Number(priority.code),
      ...(minValue !== undefined && { min: minValue }),
      ...(maxValue !== undefined && { max: maxValue }),
      ...(defaultValue !== undefined && { default: defaultValue }),
    }),
  );
};

export const transformRangeSetRuleBasedOnField = (
  fields: Partial<ProductManagementFields>,
  fieldName: string,
): DirectSalesRangeSet => {
  if (fieldName === 'sot' && fields[fieldName]?.value) {
    const fieldValue = fields[fieldName]!.value!;

    return [
      {
        min: fieldValue.minValue ? fieldValue.minValue / 100 : undefined,
        max: fieldValue.maxValue ? fieldValue.maxValue / 100 : undefined,
        default: fieldValue.defaultValue ? fieldValue.defaultValue / 100 : undefined,
      },
    ];
  }

  if (['budget', 'frames', 'impressions'].includes(fieldName)) {
    return transformTargetRange(fields, fieldName as keyof ProductManagementFields);
  }

  if (fieldName === 'networks' && fields.networks?.value?.length) {
    return transformNetworksRange(fields.networks.value);
  }

  return [];
};

export const transformAllowedValues = (
  fields: Partial<ProductManagementFields>,
): DirectSalesProductRuleAllowedSet[] => {
  return Object.keys(fields).map((fieldName) => {
    return {
      ruleType: productRuleMap[fieldName] as DirectSalesProductTemplateRuleType,
      behaviour: DirectSalesProductTemplateBehaviour.SET,
      allowedSet: transformAllowedSetRuleBasedOnField(fields, fieldName),
    };
  });
};

export const transformRangeValues = (fields: Partial<ProductManagementFields>): DirectSalesProductRuleRangeSet[] => {
  return Object.keys(fields).map((fieldName) => {
    return {
      ruleType: productRuleMap[fieldName] as DirectSalesProductTemplateRuleType,
      behaviour: DirectSalesProductTemplateBehaviour.RANGE,
      rangeSet: transformRangeSetRuleBasedOnField(fields, fieldName),
    };
  });
};

export const transformNoRestrictionFormatOptions = (
  fields: Partial<ProductManagementFields>,
): DirectSalesProductRules[] => {
  if (fields.formats) {
    const allCategories = Object.values(FormatLabel);

    return allCategories.flatMap((uniqueCategory) => ({
      ruleType: mappedFormatFilterName[uniqueCategory as string] as DirectSalesProductTemplateRuleType,
      behaviour: DirectSalesProductTemplateBehaviour.ANY,
    }));
  }

  return [];
};

export const transformNoRestrictionLocationOptions = (
  fields: Partial<ProductManagementFields>,
): DirectSalesProductRules[] => {
  if (fields.locations) {
    const locationCategories = [
      'Conurbation',
      'Country',
      'County',
      'Route Town',
      'District',
      'Town',
      'TV Area',
      'Postcode',
    ];

    return locationCategories.flatMap((uniqueCategory) => ({
      ruleType: mapLocationFilter[uniqueCategory as string] as DirectSalesProductTemplateRuleType,
      behaviour: DirectSalesProductTemplateBehaviour.ANY,
    }));
  }

  return [];
};

export const transformObjectivesRuleWrapper = ({
  behaviour,
  objectivesValues,
  objectiveSelectionPermission,
}: {
  behaviour: DirectSalesProductTemplateBehaviour;
  objectivesValues: DirectSalesTargetCriteria[];
  objectiveSelectionPermission: DirectSalesBehaviourValueKey;
}): DirectSalesProductRuleDefaultSet | DirectSalesProductRuleFixedSet => {
  return {
    ruleType: productRuleMap.objectives as DirectSalesProductTemplateRuleType,
    behaviour,
    [objectiveSelectionPermission]: objectivesValues,
  } as DirectSalesProductRuleDefaultSet | DirectSalesProductRuleFixedSet;
};

export const transformObjectives = (
  fields: Partial<ProductManagementFields>,
): DirectSalesProductRuleDefaultSet | DirectSalesProductRuleFixedSet | DirectSalesProductRules | null => {
  if (!fields.objectives) {
    return null;
  }

  const isNoRestrictions = fields.objectives.selectionPermission === SelectionPermission.ALL_OPTIONS;

  if (isNoRestrictions) {
    return {
      ruleType: productRuleMap.objectives as DirectSalesProductTemplateRuleType,
      behaviour: DirectSalesProductTemplateBehaviour.ANY,
    };
  }

  const isFixedOrDefaultObjectives =
    fields.objectives.selectionPermission === SelectionPermission.FIXED_VALUE ||
    fields.objectives.selectionPermission === SelectionPermission.DEFAULT_VALUE;

  const objectivesValues = fields.objectives.value.length ? transformObjectivesRequest(fields.objectives.value) : [];

  if (isFixedOrDefaultObjectives) {
    const behaviour =
      fields.objectives.selectionPermission === SelectionPermission.FIXED_VALUE
        ? DirectSalesProductTemplateBehaviour.FIXED
        : DirectSalesProductTemplateBehaviour.DEFAULT;
    const objectiveSelectionPermission =
      fields.objectives.selectionPermission === SelectionPermission.FIXED_VALUE ? 'fixedSet' : 'defaultSet';

    return transformObjectivesRuleWrapper({ behaviour, objectivesValues, objectiveSelectionPermission });
  }

  return null;
};

const groupRouteFrameCodesByInclude = (
  routeFrameCodes: Array<{ code: string; include?: boolean }>,
): DirectSalesProductTemplateFrameListFilter[] => {
  const grouped = routeFrameCodes.reduce<{ included: string[]; excluded: string[] }>(
    (acc, item) => {
      if (item.include) {
        acc.included.push(item.code);
      } else if (item.include === false) {
        acc.excluded.push(item.code);
      }
      return acc;
    },
    { included: [], excluded: [] },
  );

  const fixedSet: DirectSalesProductTemplateFrameListFilter[] = [];

  if (grouped.included.length > 0) {
    fixedSet.push({
      type: 'UnitListFilter',
      selectionType: DirectSalesFilterScope.INCLUDE,
      filename: null,
      unitsIds: grouped.included,
    });
  }

  if (grouped.excluded.length > 0) {
    fixedSet.push({
      type: 'UnitListFilter',
      selectionType: DirectSalesFilterScope.EXCLUDE,
      filename: null,
      unitsIds: grouped.excluded,
    });
  }

  return fixedSet;
};

const groupListFilesByIncludeAndFilename = (
  listFiles: Array<{ name?: string; fileContents?: Array<{ code: string; include?: boolean }> }>,
): DirectSalesProductTemplateFrameListFilter[] => {
  const grouped = listFiles.reduce<Record<string, { included: string[]; excluded: string[] }>>((acc, file) => {
    const key = file.name || '';

    if (!acc[key]) {
      acc[key] = { included: [], excluded: [] };
    }

    const fileContents = file.fileContents || [];
    fileContents.forEach((item) => {
      if (item.include) {
        acc[key].included.push(item.code);
      } else if (item.include === false) {
        acc[key].excluded.push(item.code);
      }
    });

    return acc;
  }, {});

  const fixedSet: DirectSalesProductTemplateFrameListFilter[] = [];

  Object.entries(grouped).forEach(([filenameKey, codes]) => {
    const filename = filenameKey || null;

    if (codes.included.length > 0) {
      fixedSet.push({
        type: 'UnitListFilter',
        selectionType: DirectSalesFilterScope.INCLUDE,
        filename,
        unitsIds: codes.included,
      });
    }

    if (codes.excluded.length > 0) {
      fixedSet.push({
        type: 'UnitListFilter',
        selectionType: DirectSalesFilterScope.EXCLUDE,
        filename,
        unitsIds: codes.excluded,
      });
    }
  });

  return fixedSet;
};

export const transformFrameListValues = (
  fields: Partial<ProductManagementFields>,
): DirectSalesProductRuleFixedSet[] => {
  const fieldValue = fields?.frameList?.value;

  if (!fieldValue) {
    return [];
  }

  const routeFrameCodesFixedSet = groupRouteFrameCodesByInclude(fieldValue.routeFrameCodes);
  const listFilesFixedSet = groupListFilesByIncludeAndFilename(fieldValue.listFiles);
  const fixedSet = [...routeFrameCodesFixedSet, ...listFilesFixedSet];

  if (fixedSet.length === 0) {
    return [];
  }

  return [
    {
      ruleType: productRuleMap.frameList as DirectSalesProductTemplateRuleType,
      behaviour: DirectSalesProductTemplateBehaviour.FIXED,
      fixedSet,
    },
  ];
};

export const transformDirectSalesProductToRules = (
  productTemplate: ProductManagementFields,
): (DirectSalesProductRules | DirectSalesProductRuleFixedSet | DirectSalesProductRuleAllowedSet)[] => {
  const enabledValues = filterAnyDisabledValues(productTemplate);

  const {
    formats: formatsAllOptions,
    locations: locationAllOptions,
    objectives: filteredObjectives,
    ...fixedAllOptions
  } = filterBySelectionPermission(enabledValues, SelectionPermission.ALL_OPTIONS);
  const allOptions = transformNoRestrictionOptions(fixedAllOptions);

  const {
    formats: formatsFixedOptions,
    locations: locationFixedOptions,
    objectives,
    frameList: frameListFixedOptions,
    ...fixedSelectionValues
  } = filterBySelectionPermission(enabledValues, SelectionPermission.FIXED_VALUE);
  const fixedValues = transformFixedValues(fixedSelectionValues);

  const transformedObjectives = transformObjectives(enabledValues);

  const allOptionsFormatValues = transformNoRestrictionFormatOptions(
    filterBySelectionPermission(enabledValues, SelectionPermission.ALL_OPTIONS),
  );
  const fixedFormatValues = transformFixedFormatValues(
    filterBySelectionPermission(enabledValues, SelectionPermission.FIXED_VALUE),
  );

  const allOptionsLocationValues = transformNoRestrictionLocationOptions(
    filterBySelectionPermission(enabledValues, SelectionPermission.ALL_OPTIONS),
  );
  const fixedLocationValues = transformFixedLocationValues(
    filterBySelectionPermission(enabledValues, SelectionPermission.FIXED_VALUE),
  );

  const allowedValues = transformAllowedValues(
    filterBySelectionPermission(enabledValues, SelectionPermission.ALLOWED_VALUES),
  );

  const rangeValues = transformRangeValues(filterBySelectionPermission(enabledValues, SelectionPermission.RANGE_VALUE));

  const frameListValues = transformFrameListValues(
    filterBySelectionPermission(enabledValues, SelectionPermission.FIXED_VALUE),
  );

  const customPoiValues = transformCustomPoiValues(
    filterBySelectionPermission(enabledValues, SelectionPermission.FIXED_VALUE),
  );

  const mergedRules = [
    ...allOptions,
    ...fixedValues,
    ...allowedValues,
    ...rangeValues,
    ...fixedFormatValues,
    ...allOptionsFormatValues,
    ...fixedLocationValues,
    ...allOptionsLocationValues,
    ...frameListValues,
    ...customPoiValues,
    transformedObjectives,
  ];

  return mergedRules.filter((rules): rules is DirectSalesProductRules => rules != null);
};
